import { DEFAULT_LANGUAGE } from '../../../shared/i18n/i18n.config';

export type LocaleZone = 'AFRICA' | 'AMERICAS' | 'ASIA_PACIFIC' | 'EUROPE' | 'MIDDLE_EAST';

export interface LocaleOption {
    /** Existing UI translation code passed to TranslateService (e.g. 'fr', 'en-US'). */
    language: string;
    /** Language endonym shown on the second line of a card (e.g. 'Français'). */
    languageLabel: string;
    /** Country endonym shown on the first line of a card (e.g. 'France', 'Schweiz'). */
    country: string;
    /** Full BCP-47 locale of the entry (e.g. 'fr-FR', 'el-GR'); the unique identifier of an option. */
    locale: string;
    zone: LocaleZone;
    /** Representative country for a language, used for the trigger label. */
    primary?: boolean;
}

const createPrimaryLocaleOption = (
    language: string,
    languageLabel: string,
    country: string,
    locale: string,
    zone: LocaleZone,
): LocaleOption => ({ language, languageLabel, country, locale, zone, primary: true });

/**
 * The supported UI languages presented country-first and grouped by geographic zone. A language can
 * appear under several countries (e.g. English across many markets, German across DE/AT/CH); those
 * entries reuse the same `language` code, since the only effect of a selection is switching the UI
 * translation. Exactly one country per language is flagged `primary` for the trigger label.
 */
export const LOCALE_OPTIONS: readonly LocaleOption[] = [
    // Africa
    { language: 'en', languageLabel: 'English', country: 'Egypt', locale: 'ar-EG', zone: 'AFRICA' },
    { language: 'en', languageLabel: 'English', country: 'Morocco', locale: 'ar-MA', zone: 'AFRICA' },
    { language: 'en', languageLabel: 'English', country: 'South Africa', locale: 'en-ZA', zone: 'AFRICA' },
    { language: 'en', languageLabel: 'English', country: 'Tunisia', locale: 'ar-TN', zone: 'AFRICA' },

    // Americas
    { language: 'en', languageLabel: 'English', country: 'Canada', locale: 'en-CA', zone: 'AMERICAS' },
    { language: 'es', languageLabel: 'Español', country: 'México', locale: 'es-MX', zone: 'AMERICAS' },
    {
        language: 'en-US',
        languageLabel: 'English US',
        country: 'United States',
        locale: 'en-US',
        zone: 'AMERICAS',
        primary: true,
    },

    // Asia Pacific
    { language: 'en', languageLabel: 'English', country: 'Australia', locale: 'en-AU', zone: 'ASIA_PACIFIC' },
    { language: 'en', languageLabel: 'English', country: 'New Zealand', locale: 'en-NZ', zone: 'ASIA_PACIFIC' },

    // Europe
    createPrimaryLocaleOption('sq', 'Shqiptare', 'Shqipëria', 'sq-AL', 'EUROPE'),
    { language: 'sq', languageLabel: 'Shqiptare', country: 'Kosova', locale: 'sq-XK', zone: 'EUROPE' },
    { language: 'nl', languageLabel: 'Nederlands', country: 'België', locale: 'nl-BE', zone: 'EUROPE' },
    { language: 'fr', languageLabel: 'Français', country: 'Belgique', locale: 'fr-BE', zone: 'EUROPE' },
    { language: 'hr', languageLabel: 'Hrvatski', country: 'Bosna i Hercegovina', locale: 'hr-BA', zone: 'EUROPE' },
    { language: 'bg', languageLabel: 'Български', country: 'България', locale: 'bg-BG', zone: 'EUROPE', primary: true },
    {
        language: 'cs',
        languageLabel: 'Čeština',
        country: 'Česká republika',
        locale: 'cs-CZ',
        zone: 'EUROPE',
        primary: true,
    },
    { language: 'da', languageLabel: 'Dansk', country: 'Danmark', locale: 'da-DK', zone: 'EUROPE', primary: true },
    {
        language: 'de',
        languageLabel: 'Deutsch',
        country: 'Deutschland',
        locale: 'de-DE',
        zone: 'EUROPE',
        primary: true,
    },
    { language: 'es', languageLabel: 'Español', country: 'España', locale: 'es-ES', zone: 'EUROPE', primary: true },
    { language: 'en', languageLabel: 'English', country: 'Estonia', locale: 'et-EE', zone: 'EUROPE' },
    { language: 'fr', languageLabel: 'Français', country: 'France', locale: 'fr-FR', zone: 'EUROPE', primary: true },
    { language: 'hr', languageLabel: 'Hrvatski', country: 'Hrvatska', locale: 'hr-HR', zone: 'EUROPE', primary: true },
    { language: 'en', languageLabel: 'English', country: 'Ireland', locale: 'en-IE', zone: 'EUROPE' },
    { language: 'en', languageLabel: 'English', country: 'Israel', locale: 'he-IL', zone: 'EUROPE' },
    { language: 'it', languageLabel: 'Italiano', country: 'Italia', locale: 'it-IT', zone: 'EUROPE', primary: true },
    { language: 'en', languageLabel: 'English', country: 'Latvia', locale: 'lv-LV', zone: 'EUROPE' },
    { language: 'en', languageLabel: 'English', country: 'Lithuania', locale: 'lt-LT', zone: 'EUROPE' },
    { language: 'fr', languageLabel: 'Français', country: 'Luxembourg', locale: 'fr-LU', zone: 'EUROPE' },
    {
        language: 'hu',
        languageLabel: 'Magyar',
        country: 'Magyarország',
        locale: 'hu-HU',
        zone: 'EUROPE',
        primary: true,
    },
    {
        language: 'nl',
        languageLabel: 'Nederlands',
        country: 'Nederland',
        locale: 'nl-NL',
        zone: 'EUROPE',
        primary: true,
    },
    { language: 'nn', languageLabel: 'Norsk', country: 'Norge', locale: 'nn-NO', zone: 'EUROPE', primary: true },
    { language: 'de', languageLabel: 'Deutsch', country: 'Österreich', locale: 'de-AT', zone: 'EUROPE' },
    { language: 'pl', languageLabel: 'Polski', country: 'Polska', locale: 'pl-PL', zone: 'EUROPE', primary: true },
    { language: 'pt', languageLabel: 'Português', country: 'Portugal', locale: 'pt-PT', zone: 'EUROPE', primary: true },
    { language: 'ro', languageLabel: 'Română', country: 'România', locale: 'ro-RO', zone: 'EUROPE', primary: true },
    { language: 'de', languageLabel: 'Deutsch', country: 'Schweiz', locale: 'de-CH', zone: 'EUROPE' },
    {
        language: 'sk',
        languageLabel: 'Slovenčina',
        country: 'Slovensko',
        locale: 'sk-SK',
        zone: 'EUROPE',
        primary: true,
    },
    { language: 'sr', languageLabel: 'Srpski', country: 'Srbija', locale: 'sr-RS', zone: 'EUROPE', primary: true },
    { language: 'sr', languageLabel: 'Srpski', country: 'Kosovo', locale: 'sr-XK', zone: 'EUROPE' },
    { language: 'fr', languageLabel: 'Français', country: 'Suisse', locale: 'fr-CH', zone: 'EUROPE' },
    { language: 'fi', languageLabel: 'Suomi', country: 'Suomi', locale: 'fi-FI', zone: 'EUROPE', primary: true },
    { language: 'sv', languageLabel: 'Svenska', country: 'Sverige', locale: 'sv-SE', zone: 'EUROPE', primary: true },
    { language: 'it', languageLabel: 'Italiano', country: 'Svizzera', locale: 'it-CH', zone: 'EUROPE' },
    { language: 'tr', languageLabel: 'Türkçe', country: 'Türkiye', locale: 'tr-TR', zone: 'EUROPE', primary: true },
    {
        language: 'en',
        languageLabel: 'English',
        country: 'United Kingdom',
        locale: 'en-GB',
        zone: 'EUROPE',
        primary: true,
    },
    { language: 'el', languageLabel: 'Ελληνικά', country: 'Ελλάδα', locale: 'el-GR', zone: 'EUROPE', primary: true },
    { language: 'el', languageLabel: 'Ελληνικά', country: 'Κύπρος', locale: 'el-CY', zone: 'EUROPE' },

    // Middle East
    { language: 'en', languageLabel: 'English', country: 'Jordan', locale: 'ar-JO', zone: 'MIDDLE_EAST' },
    { language: 'en', languageLabel: 'English', country: 'Kuwait', locale: 'ar-KW', zone: 'MIDDLE_EAST' },
    { language: 'en', languageLabel: 'English', country: 'Lebanon', locale: 'ar-LB', zone: 'MIDDLE_EAST' },
    { language: 'en', languageLabel: 'English', country: 'Qatar', locale: 'ar-QA', zone: 'MIDDLE_EAST' },
    { language: 'en', languageLabel: 'English', country: 'Saudi Arabia', locale: 'ar-SA', zone: 'MIDDLE_EAST' },
    { language: 'en', languageLabel: 'English', country: 'United Arab Emirates', locale: 'ar-AE', zone: 'MIDDLE_EAST' },
];

/** Zones in display order (alphabetical), paired with their translation keys. */
export const LOCALE_ZONES: readonly { key: LocaleZone; titleKey: string }[] = [
    { key: 'AFRICA', titleKey: 'LOCALE_ZONE_AFRICA' },
    { key: 'AMERICAS', titleKey: 'LOCALE_ZONE_AMERICAS' },
    { key: 'ASIA_PACIFIC', titleKey: 'LOCALE_ZONE_ASIA_PACIFIC' },
    { key: 'EUROPE', titleKey: 'LOCALE_ZONE_EUROPE' },
    { key: 'MIDDLE_EAST', titleKey: 'LOCALE_ZONE_MIDDLE_EAST' },
];

/** Local storage key holding the country the user explicitly picked, alongside the 'locale' language. */
export const LOCALE_COUNTRY_STORAGE_KEY = 'localeCountry';

const SUPPORTED_LANGUAGE_CODES = new Set(LOCALE_OPTIONS.map((option) => option.language));

/** Resolve an arbitrary locale (e.g. 'en-GB') to one of the supported UI language codes. */
export function resolveLanguageCode(locale: string): string {
    if (SUPPORTED_LANGUAGE_CODES.has(locale)) {
        return locale;
    }

    const baseLanguage = locale.split('-')[0];
    if (SUPPORTED_LANGUAGE_CODES.has(baseLanguage)) {
        return baseLanguage;
    }

    return DEFAULT_LANGUAGE;
}

/**
 * Resolve the option the user is currently on. When the country they explicitly picked is known
 * (and still valid for the active language) it wins; otherwise we fall back to the language's
 * primary country, then any country for that language.
 */
export function resolveSelectedOption(language: string, country?: string | null): LocaleOption | undefined {
    if (country) {
        const exact = LOCALE_OPTIONS.find((option) => option.country === country && option.language === language);
        if (exact) {
            return exact;
        }
    }

    return (
        LOCALE_OPTIONS.find((option) => option.language === language && option.primary) ??
        LOCALE_OPTIONS.find((option) => option.language === language) ??
        LOCALE_OPTIONS.find((option) => option.language === DEFAULT_LANGUAGE && option.primary)
    );
}
